// Per-device push facts live in localStorage — the same scope as the FCM
// token they describe (a token belongs to one browser profile on one device).
const MINTED_AT_KEY = 'jooice.push.tokenMintedAt';
const LAST_TOKEN_KEY = 'jooice.push.lastToken';

// FCM considers tokens stale after ~1 month; refreshing on this cadence also
// stays well clear of Android's 270-day hard expiry and recovers from
// Chrome's engagement-based permission auto-revocation.
export const TOKEN_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export type PromptStrategy = 'auto-allowed' | 'gesture-required' | 'unsupported';

export type HealReason = 'legacy' | 'stale' | 'missing-subscription';

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  const byDisplayMode = ['standalone', 'fullscreen', 'minimal-ui'].some(
    (mode) => window.matchMedia(`(display-mode: ${mode})`).matches,
  );
  return byDisplayMode || (navigator as Navigator & { standalone?: boolean }).standalone === true;
};

const isIOS = () => {
  const ua = navigator.userAgent.toLowerCase();
  return (
    /ipad|iphone|ipod/.test(ua) ||
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform)
  );
};

/**
 * Which permission-prompt flow this browser supports.
 *
 * - 'auto-allowed': Chromium allows Notification.requestPermission() outside a
 *   user gesture, so the post-login auto-prompt works.
 * - 'gesture-required': Firefox 72+ silently suppresses non-gesture prompts and
 *   WebKit (desktop Safari, iOS inside the installed PWA) can lock the
 *   permission to 'denied'; prompt only from the explicit Enable action.
 * - 'unsupported': iOS browser tab — web push only exists inside the
 *   Home-Screen-installed PWA.
 */
export const getPromptStrategy = (): PromptStrategy => {
  const ua = navigator.userAgent.toLowerCase();
  if (isIOS()) return isStandaloneDisplay() ? 'gesture-required' : 'unsupported';
  if (ua.includes('firefox')) return 'gesture-required';
  const isSafariEngine = ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
  if (isSafariEngine) return 'gesture-required';
  return 'auto-allowed';
};

/**
 * Whether the current FCM token should be silently re-minted. Only meaningful
 * when permission is already granted — healing never shows a prompt.
 * 'missing-subscription' wins over age checks: a dead push subscription must
 * heal regardless of how fresh the token claims to be.
 */
export const needsTokenHeal = (input: {
  permission: NotificationPermission;
  mintedAt: number | null;
  hasSubscription: boolean;
}): false | HealReason => {
  if (input.permission !== 'granted') return false;
  if (!input.hasSubscription) return 'missing-subscription';
  if (input.mintedAt === null) return 'legacy';
  if (Date.now() - input.mintedAt > TOKEN_MAX_AGE_MS) return 'stale';
  return false;
};

export const recordTokenMint = (token: string) => {
  try {
    localStorage.setItem(MINTED_AT_KEY, String(Date.now()));
    localStorage.setItem(LAST_TOKEN_KEY, token);
  } catch {
    // Storage unavailable (private mode, quota) — the heal check will treat
    // the next visit as 'legacy', which is the safe direction.
  }
};

export const readTokenMintedAt = (): number | null => {
  try {
    const raw = localStorage.getItem(MINTED_AT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

export const readLastToken = (): string | null => {
  try {
    return localStorage.getItem(LAST_TOKEN_KEY);
  } catch {
    return null;
  }
};
